// TODO: this can be shared with frontend
type FormatDollarOptions = {
  hideZeroCents?: boolean;
  zeroValue?: string;
};

const defaultOptions: FormatDollarOptions = {
  hideZeroCents: true,
  zeroValue: '0',
};

export function formatDollars(amountInDollars: number | undefined, options?: FormatDollarOptions) {
  const optionsToUse = { ...defaultOptions, ...options };
  const formatted =
    amountInDollars === undefined
      ? optionsToUse.zeroValue
      : `${new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
        }).format(amountInDollars)} `;
  return optionsToUse.hideZeroCents ? formatted?.replace('.00', '') : formatted;
}

export type QueryString = { name: string; value?: string | number | null };
export const getQueryString = (query?: QueryString[]) => {
  let queryString = '?';
  query?.forEach((q) => {
    if (q.value) {
      queryString += `${q.name}=${q.value}&`;
    }
  });

  return queryString.slice(0, -1);
};

// brighteSiteSlug and brighteSiteSubdomain come from the partner's
// enterpriseGroups configuration (enterprise service). Validated before use
// so a malformed upstream value can't be concatenated into the URL.
const SAFE_SLUG_RE = /^[a-z0-9][a-z0-9-/]*$/i;
const SAFE_SUBDOMAIN_RE = /^[a-z0-9][a-z0-9-]*$/i;

type BrighteSitePartnerUrlOptions = {
  // Genuinely provisioned subdomain (e.g. "commbank" for commbank.brighte.com.au).
  // Only set for the small set of partners with a real dedicated subdomain;
  // takes priority over brighteSiteSlug when both are set.
  brighteSiteSubdomain?: string | null;
  // Slug for a "brighte.com.au/{slug}" path. Absent means the plain Brighte
  // site, no path suffix.
  brighteSiteSlug?: string | null;
};

export const getBrighteSitePartnerUrl = ({
  brighteSiteSubdomain,
  brighteSiteSlug,
}: BrighteSitePartnerUrlOptions = {}) => {
  const domain = import.meta.env.VITE_BRIGHTE_SITE_DOMAIN;
  const stage = import.meta.env.VITE_STAGE;
  const env = stage === 'prod' ? '' : `${stage}.`;

  if (brighteSiteSubdomain && SAFE_SUBDOMAIN_RE.test(brighteSiteSubdomain)) {
    return `https://${env}${brighteSiteSubdomain}.${domain}`;
  }

  const slugPath = brighteSiteSlug && SAFE_SLUG_RE.test(brighteSiteSlug) ? `/${brighteSiteSlug}` : '';
  return `https://${env}${domain}${slugPath}`;
};

export const formatAddress = (address: { postcode: string; suburb?: string }) => {
  const { postcode, suburb } = address;
  let formattedAddress = '';
  if (suburb && suburb.trim() !== '') {
    formattedAddress += `${suburb}, `;
  }
  if (postcode && postcode.trim() !== '') {
    formattedAddress += postcode;
  }

  return formattedAddress.replace(/^,\s*/, '');
};

export const formatPhoneNumber = (phoneNumber: string) => {
  // Remove all spaces first
  const cleanNumber = phoneNumber.replace(/\s/g, '');

  // If already in international format, return as is
  if (cleanNumber.startsWith('+61')) {
    return cleanNumber;
  }
  // Handle numbers starting with 0 or just the mobile number (9 digits starting with 1-9)
  return cleanNumber.replace(/^(?:0|(?=[1-9]\d{8}$))([1-9]\d{8})$/, '+61$1');
};

export const formatCensoredEmail = (email: string) => {
  const [username, domain] = email.split('@');
  if (!username || !domain) return email;

  // Don't censor if username has only one character
  if (username.length <= 1) return email;

  // For usernames with more than 4 characters, show first 3 characters
  // For usernames with 2-4 characters, show first 1 character
  const visibleChars = username.length > 4 ? 3 : 1;
  const visiblePart = username.slice(0, visibleChars);
  const hiddenPart = '*'.repeat(Math.max(0, username.length - visibleChars));

  return `${visiblePart}${hiddenPart}@${domain}`;
};
